import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { X } from 'lucide-react';

// Toast positioning types
export const POSITIONS = {
  TOP_LEFT: 'top-left',
  TOP_CENTER: 'top-center',
  TOP_RIGHT: 'top-right',
  BOTTOM_LEFT: 'bottom-left',
  BOTTOM_CENTER: 'bottom-center',
  BOTTOM_RIGHT: 'bottom-right',
};

// Toast types
export const TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  INFO: 'info',
  WARNING: 'warning',
};

// Create a global event emitter for toasts
class ToastEventEmitter {
  constructor() {
    this.listeners = [];
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  emit(event) {
    this.listeners.forEach((listener) => listener(event));
  }
}

// Create a single instance of the event emitter
export const toastEmitter = new ToastEventEmitter();

// Helper function to show toasts from anywhere
export const toastActions = {
  show: (text, options = {}) => {
    toastEmitter.emit({ text, ...options });
  },
  success: (text, options = {}) => {
    toastEmitter.emit({ text, type: TYPES.SUCCESS, ...options });
  },
  error: (text, options = {}) => {
    toastEmitter.emit({ text, type: TYPES.ERROR, ...options });
  },
  info: (text, options = {}) => {
    toastEmitter.emit({ text, type: TYPES.INFO, ...options });
  },
  warning: (text, options = {}) => {
    toastEmitter.emit({ text, type: TYPES.WARNING, ...options });
  },
};

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-20px);
  }
`;

// Get position styles based on position prop
const getPositionStyles = (position) => {
  switch (position) {
    case POSITIONS.TOP_LEFT:
      return css`
        top: 20px;
        left: 20px;
      `;
    case POSITIONS.TOP_CENTER:
      return css`
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
      `;
    case POSITIONS.TOP_RIGHT:
      return css`
        top: 20px;
        right: 20px;
      `;
    case POSITIONS.BOTTOM_LEFT:
      return css`
        bottom: 20px;
        left: 20px;
      `;
    case POSITIONS.BOTTOM_CENTER:
      return css`
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
      `;
    case POSITIONS.BOTTOM_RIGHT:
      return css`
        bottom: 20px;
        right: 20px;
      `;
    default:
      return css`
        top: 20px;
        right: 20px;
      `;
  }
};

// Get color styles based on type
const getTypeStyles = (type) => {
  switch (type) {
    case TYPES.SUCCESS:
      return css`
        background-color: #4caf50;
        color: white;
      `;
    case TYPES.ERROR:
      return css`
        background-color: #f44336;
        color: white;
      `;
    case TYPES.INFO:
      return css`
        background-color: #2196f3;
        color: white;
      `;
    case TYPES.WARNING:
      return css`
        background-color: #ff9800;
        color: white;
      `;
    default:
      return css`
        background-color: #333;
        color: white;
      `;
  }
};

// Styled components
const ToastContainer = styled.div`
  position: fixed;
  z-index: 9999;
  ${({ position }) => getPositionStyles(position)};
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 100%;
`;

const ToastItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  min-width: 250px;
  max-width: 350px;
  ${({ type }) => getTypeStyles(type)};
  animation: ${({ isClosing }) => (isClosing ? fadeOut : fadeIn)} 0.3s ease;

  @media (max-width: 480px) {
    min-width: auto;
    max-width: calc(100vw - 40px);
  }
`;

const ToastContent = styled.div`
  flex: 1;
  margin-right: 8px;
`;

const ToastMessage = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  font-family: 'JetBrains Mono Regular';
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: inherit;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

// Individual Toast
const ToastNotification = ({ id, text, type, onClose, isClosing }) => {
  return (
    <ToastItem type={type} isClosing={isClosing}>
      <ToastContent>
        <ToastMessage>{text}</ToastMessage>
      </ToastContent>
      <CloseButton onClick={() => onClose(id)} aria-label="Close toast">
        <X size={18} />
      </CloseButton>
    </ToastItem>
  );
};

/**
 * ToastContainer Component - Place this once in your App.js
 *
 * @param {Object} props
 * @param {string} [props.position='top-right'] - Default position for all toasts
 * @param {number} [props.duration=5000] - Default duration before auto-close
 */
const ToastManager = ({ position = POSITIONS.TOP_RIGHT, duration = 5000 }) => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    // Subscribe to toast events
    const unsubscribe = toastEmitter.subscribe((toastOptions) => {
      const id = Date.now().toString();
      const toastDuration = toastOptions.duration || duration;

      // Add new toast
      setToasts((prevToasts) => [
        ...prevToasts,
        {
          id,
          text: toastOptions.text,
          type: toastOptions.type || TYPES.INFO,
          duration: toastDuration,
          position: toastOptions.position || position,
          isClosing: false,
        },
      ]);

      // Auto-close after duration
      if (toastOptions.autoClose !== false) {
        setTimeout(() => {
          handleCloseToast(id);
        }, toastDuration);
      }
    });

    return unsubscribe;
  }, [position, duration]);

  const handleCloseToast = (id) => {
    setToasts((prevToasts) =>
      prevToasts.map((toast) => (toast.id === id ? { ...toast, isClosing: true } : toast))
    );

    // Remove toast after animation completes
    setTimeout(() => {
      setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
    }, 300);
  };

  // Group toasts by position
  const toastsByPosition = toasts.reduce((acc, toast) => {
    const pos = toast.position || position;
    if (!acc[pos]) acc[pos] = [];
    acc[pos].push(toast);
    return acc;
  }, {});

  return (
    <>
      {Object.entries(toastsByPosition).map(([pos, positionToasts]) => (
        <ToastContainer key={pos} position={pos}>
          {positionToasts.map((toast) => (
            <ToastNotification
              key={toast.id}
              id={toast.id}
              text={toast.text}
              type={toast.type}
              isClosing={toast.isClosing}
              onClose={handleCloseToast}
            />
          ))}
        </ToastContainer>
      ))}
    </>
  );
};

export default ToastManager;
